import React, { useState, useEffect } from 'react'
import toast from 'react-hot-toast'
import { Button } from '@/components/Button'
import { Card } from '@/components/Card'
import { geolocationService } from '@/services/geolocation'
import { MapPin, Trash2, ExternalLink, Search, X } from 'lucide-react'

interface GeolocationItem {
  id: number
  latitude: number
  longitude: number
  entity_type: string
  entity_id: number
  nome_unidade?: string
  sigla_unidade?: string
  id_file?: number
  file?: {
    id: number
    field_name: string
    client_name: string
    type: string
    subtype: string
  }
  accuracy?: number
  source?: string
  created_at: string
  updated_at: string
}

export function GeolocationListPage() {
  const [geolocations, setGeolocations] = useState<GeolocationItem[]>([])
  const [loading, setLoading] = useState(true)
  const [deleting, setDeleting] = useState<number | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [searchTerm, setSearchTerm] = useState('')
  const [searchType, setSearchType] = useState<'nome_unidade' | 'sigla_unidade'>('nome_unidade')

  const loadGeolocations = async (page = 1, searchQuery = '', searchField = 'nome_unidade') => {
    try {
      setLoading(true)
      console.log('🔍 Carregando geolocalizações...', { page, searchQuery, searchField })
      
      let response
      
      if (searchQuery && searchQuery.trim()) {
        // Usar as rotas específicas de busca do backend
        console.log('🔍 Usando rotas específicas de busca do backend...')
        
        switch (searchField) {
          case 'sigla_unidade':
            response = await geolocationService.searchBySigla(searchQuery.trim(), page, 10)
            break
          case 'nome_unidade':
            response = await geolocationService.searchByNome(searchQuery.trim(), page, 10)
            break
          default:
            // Busca combinada para outros casos
            response = await geolocationService.searchByTermo(searchQuery.trim(), page, 10)
        }
      } else {
        // Listagem normal sem busca
        response = await geolocationService.getAll(page, 10)
      }
      
      console.log('📡 Resposta da API:', response)
      
      console.log('🔍 Verificando resposta:', {
        success: response.success,
        hasData: !!response.data,
        dataType: typeof response.data,
        isArray: Array.isArray(response.data),
        dataKeys: response.data ? Object.keys(response.data) : []
      })

      if (response.success && response.data) {
        console.log('✅ Dados recebidos:', response.data)
        
        // Verificar se é um array direto
        if (Array.isArray(response.data)) {
          console.log('📋 Dados diretos (array):', response.data)
          setGeolocations(response.data)
          setCurrentPage(1)
          setTotalPages(1)
        }
        // Verificar se é paginação
        else if (response.data.data && Array.isArray(response.data.data)) {
          console.log('📄 Dados paginados:', response.data.data)
          setGeolocations(response.data.data)
          setCurrentPage(response.data.meta?.current_page || 1)
          setTotalPages(response.data.meta?.last_page || 1)
        }
        // Se não é nem array nem paginação, tentar usar os dados diretamente
        else {
          console.log('⚠️ Formato inesperado, tentando usar dados diretamente:', response.data)
          setGeolocations([])
        }
      } else {
        console.log('❌ Resposta sem sucesso ou sem dados:', response)
        setGeolocations([])
      }
      
    } catch (error: any) {
      console.error('❌ Erro ao carregar geolocalizações:', error)
      toast.error('Erro ao carregar localizações')
      setGeolocations([])
    } finally {
      setLoading(false)
    }
  }

  const handleSearch = () => {
    console.log('🔍 Executando busca:', { searchTerm, searchType })
    if (searchTerm.trim()) {
      setCurrentPage(1)
      loadGeolocations(1, searchTerm.trim(), searchType)
    } else {
      console.log('🔄 Limpando busca - carregando todas as geolocalizações')
      loadGeolocations(1)
    }
  }

  const handleClearSearch = () => {
    setSearchTerm('')
    setCurrentPage(1)
    loadGeolocations(1)
  }

  const handleDelete = async (id: number) => {
    if (!confirm('Tem certeza que deseja remover esta localização?')) {
      return
    }

    try {
      setDeleting(id)
      await geolocationService.delete(id)
      toast.success('Localização removida com sucesso!')
      await loadGeolocations(currentPage, searchTerm, searchType)
    } catch (error: any) {
      console.error('Erro ao deletar:', error)
      toast.error('Erro ao remover localização')
    } finally {
      setDeleting(null)
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('pt-BR')
  }

  const formatCoordinates = (lat: number, lng: number) => {
    return `${lat.toFixed(6)}, ${lng.toFixed(6)}`
  }

  const getAccuracyColor = (accuracy?: number) => {
    if (!accuracy) return 'text-gray-500'
    if (accuracy <= 10) return 'text-green-600'
    if (accuracy <= 50) return 'text-yellow-600'
    return 'text-red-600'
  }

  const getAccuracyLabel = (accuracy?: number) => {
    if (!accuracy) return 'Não informada'
    if (accuracy <= 10) return 'Excelente'
    if (accuracy <= 50) return 'Boa'
    return 'Baixa'
  }

  useEffect(() => {
    loadGeolocations()
  }, [])

  // Função para lidar com Enter na busca
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch()
    }
  }

  return (
    <div className="w-full">
      {/* Cabeçalho da página */}
      <div className="mb-8">
        <div className="flex items-center space-x-4 mb-4">
          <MapPin className="w-8 h-8 text-blue-600" />
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Localizações</h1>
            <p className="text-gray-600">Gerenciar localizações geográficas</p>
          </div>
        </div>
        
        {/* Barra de busca */}
        <div className="mb-6">
          <Card>
            <div className="p-4">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1">
                  <label htmlFor="searchType" className="block text-sm font-medium text-gray-700 mb-2">
                    Buscar por:
                  </label>
                  <select
                    id="searchType"
                    value={searchType}
                    onChange={(e) => setSearchType(e.target.value as 'nome_unidade' | 'sigla_unidade')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="nome_unidade">Nome da Unidade</option>
                    <option value="sigla_unidade">Sigla da Unidade</option>
                  </select>
                </div>
                
                <div className="flex-2">
                  <label htmlFor="searchTerm" className="block text-sm font-medium text-gray-700 mb-2">
                    Termo de busca:
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      id="searchTerm"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      onKeyPress={handleKeyPress}
                      placeholder={`Digite o ${searchType === 'nome_unidade' ? 'nome' : 'sigla'} da unidade...`}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                    <Button
                      type="button"
                      onClick={handleSearch}
                      className="px-4 py-2"
                    >
                      <Search className="w-4 h-4" />
                    </Button>
                    {searchTerm && (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleClearSearch}
                        className="px-4 py-2"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </div>
              
              {searchTerm && (
                <div className="mt-3 text-sm text-gray-600">
                  <span className="inline-flex items-center px-2 py-1 rounded-full bg-blue-100 text-blue-800">
                    🔍 Buscando por {searchType === 'nome_unidade' ? 'nome' : 'sigla'}: "{searchTerm}"
                  </span>
                  <span className="ml-2 text-xs text-gray-500">
                    ({geolocations.length} resultado{geolocations.length !== 1 ? 's' : ''} encontrado{geolocations.length !== 1 ? 's' : ''})
                  </span>
                  {geolocations.length === 0 && (
                    <div className="mt-2 text-xs text-red-600">
                      ⚠️ Nenhum resultado encontrado para esta busca
                    </div>
                  )}
                </div>
              )}
            </div>
          </Card>
        </div>
        
        <div className="flex justify-end">
          <Button
            type="button"
            onClick={() => window.location.href = '/geolocation'}
          >
            <MapPin className="w-4 h-4 mr-2" />
            Nova Localização
          </Button>
        </div>
      </div>

      {/* Conteúdo principal */}
      <Card>
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="mt-2 text-gray-600">Carregando...</p>
          </div>
        ) : geolocations.length === 0 ? (
          <div className="text-center py-12">
            <MapPin className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {searchTerm ? 'Nenhum resultado encontrado' : 'Nenhuma localização cadastrada'}
            </h3>
            <p className="text-gray-600 mb-4">
              {searchTerm 
                ? `Não foram encontradas localizações para "${searchTerm}" em ${searchType === 'nome_unidade' ? 'nome' : 'sigla'} da unidade.`
                : 'Comece adicionando sua primeira localização'
              }
            </p>
            {searchTerm ? (
              <div className="space-x-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleClearSearch}
                >
                  <X className="w-4 h-4 mr-2" />
                  Limpar Busca
                </Button>
                <Button
                  onClick={() => window.location.href = '/geolocation'}
                >
                  <MapPin className="w-4 h-4 mr-2" />
                  Adicionar Localização
                </Button>
              </div>
            ) : (
              <Button
                onClick={() => window.location.href = '/geolocation'}
              >
                <MapPin className="w-4 h-4 mr-2" />
                Adicionar Localização
              </Button>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {geolocations.map((geo) => (
              <div
                key={geo.id}
                className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <MapPin className="w-5 h-5 text-blue-600" />
                      <h3 className="font-medium text-gray-900">
                        {formatCoordinates(geo.latitude, geo.longitude)}
                      </h3>
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        geo.entity_type === 'servidor' 
                          ? 'bg-purple-100 text-purple-700'
                          : geo.entity_type === 'discente'
                          ? 'bg-green-100 text-green-700'
                          : 'bg-blue-100 text-blue-700'
                      }`}>
                        {geo.entity_type === 'servidor' ? 'Servidor' : 
                         geo.entity_type === 'discente' ? 'Discente' : 'Usuário'}
                      </span>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-600">
                      <div>
                        <p><strong>Unidade:</strong></p>
                        <p>{geo.nome_unidade || 'Não informado'}</p>
                        {geo.sigla_unidade && (
                          <p><strong>Sigla:</strong> {geo.sigla_unidade}</p>
                        )}
                        {geo.file && (
                          <div className="mt-2">
                            <p><strong>Arquivo:</strong></p>
                            <a 
                              href={`/api/files/${geo.file.id}/download`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-800 underline text-xs"
                            >
                              📎 {geo.file.client_name}
                            </a>
                          </div>
                        )}
                      </div>
                      
                      <div>
                        <p><strong>Precisão:</strong></p>
                        <p className={getAccuracyColor(geo.accuracy)}>
                          {geo.accuracy ? `${geo.accuracy.toFixed(0)}m - ${getAccuracyLabel(geo.accuracy)}` : 'Não informada'}
                        </p>
                        <p><strong>Origem:</strong> {geo.source || 'manual'}</p>
                      </div>
                      
                      <div>
                        <p><strong>Cadastrado em:</strong></p>
                        <p>{formatDate(geo.created_at)}</p>
                        {geo.updated_at !== geo.created_at && (
                          <p><strong>Atualizado:</strong> {formatDate(geo.updated_at)}</p>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2 ml-4">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const url = `https://www.google.com/maps?q=${geo.latitude},${geo.longitude}`
                        window.open(url, '_blank')
                      }}
                    >
                      <ExternalLink className="w-4 h-4" />
                    </Button>
                    
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => handleDelete(geo.id)}
                      loading={deleting === geo.id}
                      className="text-red-600 border-red-300 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
            
            {/* Paginação */}
            {totalPages > 1 && (
              <div className="flex justify-center items-center space-x-2 mt-6">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={currentPage <= 1}
                  onClick={() => loadGeolocations(currentPage - 1, searchTerm, searchType)}
                >
                  Anterior
                </Button>
                
                <span className="text-sm text-gray-600">
                  Página {currentPage} de {totalPages}
                </span>
                
                <Button
                  variant="outline"
                  size="sm"
                  disabled={currentPage >= totalPages}
                  onClick={() => loadGeolocations(currentPage + 1, searchTerm, searchType)}
                >
                  Próxima
                </Button>
              </div>
            )}
          </div>
        )}
      </Card>
    </div>
  )
}
